/**
 * <ConsciousImprint /> — Step 2 of the Absorption Flow.
 *
 * NEW — first conscious exposure to today's content. The user scrolls
 * through full-card tiles showing the image + the answer (no blur, no
 * question prompt). Same visual format as the quiz answer side, just
 * with no flip mechanic. The user reads, takes it in, taps Continue.
 *
 * Per spec:
 *   - Show ALL learning cards (answer side only, no blur)
 *   - Image + text composition matching the quiz answer card exactly
 *   - Slow scroll layout
 *   - No interaction needed beyond "I've taken it in"
 */
import { useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "./journeyTheme";

const API = process.env.REACT_APP_BACKEND_URL;

function buildList(content) {
  const flash    = (content?.cards?.flash    || []).map((c) => ({ ...c, kind: "flash" }));
  const scenario = (content?.cards?.scenario || []).map((c) => ({ ...c, kind: "scenario" }));
  const precision= (content?.cards?.precision|| []).map((c) => ({ ...c, kind: "precision" }));
  return [...flash, ...scenario, ...precision].filter((c) => c.back);
}

export default function ConsciousImprint({ content, onContinue }) {
  const list = useMemo(() => buildList(content), [content]);

  if (list.length === 0) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: P.bgWash }}
      >
        <button
          onClick={onContinue}
          className="text-[11px] tracking-[0.32em] uppercase"
          style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
          data-testid="imprint-passthrough"
        >
          continue →
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen flex flex-col"
      style={{ background: P.bgWash }}
      data-testid="conscious-imprint"
    >
      {/* Sticky header */}
      <div
        className="sticky top-0 z-10 px-6 pt-12 pb-5"
        style={{
          background:
            "linear-gradient(180deg, rgba(13,27,42,1) 70%, rgba(13,27,42,0) 100%)",
        }}
      >
        <div className="max-w-md mx-auto flex items-center justify-between">
          <p
            className="text-[10px] uppercase tracking-[0.36em]"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          >
            Take it in
          </p>
          <p
            className="text-[10px] uppercase tracking-[0.32em]"
            style={{ color: "rgba(255,255,255,0.45)", fontFamily: JOURNEY_FONT_LABEL }}
          >
            first exposure
          </p>
        </div>
      </div>

      {/* Scrollable card list */}
      <div className="flex-1 overflow-y-auto px-6 pb-40">
        <div className="max-w-md mx-auto space-y-6 pt-3">
          {list.map((card, i) => {
            const img = card.image_url
              ? (card.image_url.startsWith("http") ? card.image_url : `${API}${card.image_url}`)
              : null;
            return (
              <motion.div
                key={card.id || i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.7,
                  ease: [0.16, 1, 0.3, 1],
                  delay: Math.min(i * 0.06, 0.5),
                }}
                className="rounded-3xl overflow-hidden"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.30)",
                }}
                data-testid={`imprint-card-${i}`}
              >
                {/* Image (top) — only render when the card has its own image.
                    No fallback photo per design system: clean text-only is
                    better than the same horizon photo on every card. */}
                {img && (
                  <div
                    className="relative"
                    style={{
                      height: 200,
                      backgroundImage: `url(${img})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <div
                      className="absolute inset-x-0 bottom-0"
                      style={{
                        height: "55%",
                        background:
                          "linear-gradient(180deg, rgba(13,27,42,0) 0%, rgba(13,27,42,0.85) 100%)",
                      }}
                      aria-hidden
                    />
                  </div>
                )}

                {/* Text — Situation (bold) + Answer (lighter) */}
                <div className="px-5 pt-5 pb-6">
                  {card.front && (
                    <p
                      className="leading-[1.34] mb-3"
                      style={{
                        color: "#FFFFFF",
                        fontFamily: JOURNEY_FONT_HEAD,
                        fontWeight: 600,
                        fontSize: 20,
                        letterSpacing: "-0.005em",
                      }}
                      data-testid={`imprint-front-${i}`}
                    >
                      {card.front}
                    </p>
                  )}
                  <p
                    className="text-[10px] uppercase tracking-[0.32em] mb-2"
                    style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
                  >
                    What it means
                  </p>
                  <p
                    className="leading-[1.55]"
                    style={{
                      color: "rgba(255,255,255,0.78)",
                      fontFamily: JOURNEY_FONT_BODY,
                      fontWeight: 300,
                      fontSize: 15,
                      letterSpacing: "0.005em",
                    }}
                    data-testid={`imprint-back-${i}`}
                  >
                    {card.back}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Bottom continue button */}
      <div
        className="absolute bottom-0 left-0 right-0 px-6 pb-7 pt-12 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(13,27,42,0) 0%, rgba(13,27,42,0.95) 60%)",
        }}
      >
        <div className="max-w-md mx-auto pointer-events-auto">
          <motion.button
            onClick={onContinue}
            whileTap={{ scale: 0.985 }}
            whileHover={{ scale: 1.012 }}
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
            className="w-full rounded-full py-[14px] flex items-center justify-center gap-2"
            style={{
              background: `linear-gradient(135deg, ${P.accentBlue} 0%, ${P.accentBlueDeep} 100%)`,
              color: "#FFFFFF",
              fontFamily: JOURNEY_FONT_BODY,
              fontWeight: 500,
              fontSize: 14,
              letterSpacing: "0.01em",
              border: "1px solid rgba(77,163,255,0.55)",
              boxShadow:
                "0 12px 30px rgba(46,123,214,0.40), 0 0 24px rgba(77,163,255,0.30)",
            }}
            data-testid="imprint-continue-btn"
          >
            I've taken it in
            <ArrowRight size={15} strokeWidth={2.2} />
          </motion.button>
        </div>
      </div>
    </div>
  );
}
